import { useCallback, useEffect, useRef, useState } from 'react';
import {
  subscribeAppSyncEvents,
  publishAppSyncEvent,
  appsyncEventEndpoints,
  type AppSyncEventsUnsubscribe,
} from '../services/appsyncEvents';

export type AgentChannelMessage = Record<string, unknown>;

/**
 * Soft cap on the in-memory envelope buffer per room. Long-running rooms
 * (multi-hour executions, dozens of approvals) can stream tens of thousands
 * of events; rendering history is paged from `/networks/{id}/channel-events`,
 * so the live buffer only needs to hold the recent window. We trim the
 * oldest entries when we exceed this size — `correlationId` survives because
 * it lives on each envelope, not on the buffer.
 */
export const AGENT_CHANNEL_SOFT_CAP = 500;

/**
 * Stable per-envelope dedupe key. Mirrors the priority order used by
 * `services/workbenchRealtime.dedupeKey` so the two pipelines suppress the
 * same logical duplicate when both transports deliver it (legacy WS +
 * AppSync replay during reconnect catch-up). Falls back to a composite key
 * so envelopes that lost their server-stamped `eventId` still collapse on
 * exact retransmits.
 */
export function agentChannelEventKey(raw: AgentChannelMessage): string {
  if (!raw || typeof raw !== 'object') return `raw:${String(raw)}`;
  const ev = raw as Record<string, unknown>;
  if (typeof ev.eventId === 'string' && ev.eventId.length > 0) return `ev:${ev.eventId}`;
  if (typeof ev._appsyncEventId === 'string' && ev._appsyncEventId.length > 0) {
    return `as:${ev._appsyncEventId}`;
  }
  const t = String(ev.eventType ?? ev.type ?? '');
  const ts = String(ev.timestamp ?? ev.created_at ?? '');
  const corr = String(ev.correlationId ?? '');
  return `c:${t}:${corr}:${ts}`;
}

/**
 * Cognito scope segment for AppSync channel path — must match backend
 * ``status_broadcaster`` / ``stepflow_core.events.scope_segment`` and the
 * sync stack ``onSubscribe`` authorizer (tenant id or ``user-{sub}``).
 * AppSync channel segments are strictly ``[A-Za-z0-9-]`` — never use
 * underscores in the personal fallback.
 */
export function appsyncChannelScope(
  activeTenantId: string | undefined,
  cognitoSub: string | undefined,
): string | undefined {
  const t = (activeTenantId || '').trim();
  if (t) return t;
  const sub = (cognitoSub || '').trim();
  return sub ? `user-${sub}` : undefined;
}

export const DEFAULT_POOL_ID = 'default';

function normalizePoolId(poolId: string | undefined): string {
  const p = (poolId || '').trim();
  return p || DEFAULT_POOL_ID;
}

/**
 * Compare two envelopes for chronological order. Falls back to insertion
 * order (returns 0) when neither has a parseable timestamp so callers
 * preserve arrival order for mid-flight transitions like LOG_BATCH bursts.
 */
function compareEnvelopeTimestamps(a: AgentChannelMessage, b: AgentChannelMessage): number {
  const ta = parseEnvelopeTs(a);
  const tb = parseEnvelopeTs(b);
  if (ta === null || tb === null) return 0;
  return ta - tb;
}

function parseEnvelopeTs(raw: AgentChannelMessage): number | null {
  if (!raw || typeof raw !== 'object') return null;
  const o = raw as Record<string, unknown>;
  const v = o.timestamp ?? o.created_at ?? o.serverTimestamp ?? o.server_timestamp;
  if (typeof v === 'number' && Number.isFinite(v)) return v;
  if (typeof v === 'string' && v.length > 0) {
    const n = Date.parse(v);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

function parseAgentEventEntry(e: unknown): AgentChannelMessage {
  if (typeof e === 'string') {
    try {
      return JSON.parse(e) as AgentChannelMessage;
    } catch {
      return { raw: e } as AgentChannelMessage;
    }
  }
  if (e && typeof e === 'object' && !Array.isArray(e)) {
    return e as AgentChannelMessage;
  }
  return { raw: e } as AgentChannelMessage;
}

/**
 * AppSync Events — subscribe on ``/default/agent/{scope}/{networkId}/{poolId}``.
 *
 * ``poolId`` is the runtime network-tenant (customer pool); pass ``"default"``
 * for creator/build views where no runtime tenant has been selected yet.
 * Must stay in sync with the sync-stack ``onSubscribe`` authorizer and the
 * backend ``stepflow_core.events`` scope derivation.
 *
 * Transport is the shared ``services/appsyncEvents`` singleton, so mounting
 * this hook alongside ``useUserChannel`` / ``useWorkbenchChannel`` adds a
 * multiplexed subscription on the existing WebSocket rather than opening
 * a brand new one.
 *
 * Env (from SSM / CI): ``VITE_APPSYNC_EVENTS_HTTP_DNS``,
 * ``VITE_APPSYNC_EVENTS_REALTIME_DNS``.
 */
export function useAgentChannel(
  networkId: string | undefined,
  channelScope: string | undefined,
  poolId?: string,
) {
  const effectivePoolId = normalizePoolId(poolId);
  const [messages, setMessages] = useState<AgentChannelMessage[]>([]);
  const [connected, setConnected] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Dedupe ledger lives in a ref so the soft cap doesn't repeatedly trip
  // React renders. Cleared in lockstep with `messages` on pool/scope pivot.
  const seenKeysRef = useRef<Set<string>>(new Set());

  const { httpDns, realtimeDns } = appsyncEventEndpoints();

  useEffect(() => {
    seenKeysRef.current = new Set();
    setMessages([]);
  }, [networkId, channelScope, effectivePoolId]);

  useEffect(() => {
    if (!networkId || !channelScope || !httpDns || !realtimeDns) {
      setConnected(false);
      return undefined;
    }

    setError(null);
    const channelPath = `/default/agent/${channelScope}/${networkId}/${effectivePoolId}`;

    let active = true;
    const unsubscribe: AppSyncEventsUnsubscribe = subscribeAppSyncEvents(
      httpDns,
      realtimeDns,
      channelPath,
      {
        onData: (events) => {
          if (!active) return;
          const fresh: AgentChannelMessage[] = [];
          for (const e of events) {
            const parsed = parseAgentEventEntry(e);
            const key = agentChannelEventKey(parsed);
            if (seenKeysRef.current.has(key)) continue;
            seenKeysRef.current.add(key);
            fresh.push(parsed);
          }
          if (!fresh.length) return;
          // Most arrivals are already chronological; sort defensively so a
          // late-delivered envelope doesn't break the reducer's monotonic
          // cursor (`AgenticNetworkRoom.msgCursorRef`).
          fresh.sort(compareEnvelopeTimestamps);
          setMessages((prev) => {
            const next = prev.length + fresh.length > AGENT_CHANNEL_SOFT_CAP
              ? [...prev, ...fresh].slice(-AGENT_CHANNEL_SOFT_CAP)
              : [...prev, ...fresh];
            return next;
          });
        },
        onStatus: (status) => {
          if (!active) return;
          setConnected(status === 'connected');
        },
        onError: (err, kind) => {
          if (!active) return;
          setError(
            typeof err === 'string'
              ? err
              : JSON.stringify(err ?? { kind }),
          );
        },
      },
    );

    return () => {
      active = false;
      unsubscribe();
    };
  }, [networkId, channelScope, effectivePoolId, httpDns, realtimeDns]);

  const sendMessage = useCallback(
    async (payload: Record<string, unknown>) => {
      if (!networkId || !channelScope || !httpDns) {
        throw new Error('Missing channel or VITE_APPSYNC_EVENTS_HTTP_DNS');
      }
      const channel = `default/agent/${channelScope}/${networkId}/${effectivePoolId}`;
      await publishAppSyncEvent(httpDns, channel, payload);
    },
    [networkId, channelScope, effectivePoolId, httpDns],
  );

  return { messages, sendMessage, connected, error };
}
